/**
 * useAppJobs Hook
 *
 * Custom React Query hook for fetching appJobs list.
 */

import { useQuery } from '@tanstack/react-query';
import { appJobService } from '../../../api/app-job.service';
import type { AppJobListQuery } from '../../../types/app-job.types';

/**
 * Hook for fetching all appJobs for a tenant
 * @param tenantId - Tenant ID
 * @param query - Optional query parameters (limit, cursor)
 */
export const useAppJobs = (tenantId: string, query?: AppJobListQuery) => {
  return useQuery({
    queryKey: ['appJobs', tenantId, query],
    queryFn: async () => {
      const result = await appJobService.listAppJobs(tenantId, query);
      return result;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching appJobs for a specific application
 * @param tenantId - Tenant ID
 * @param applicationId - Application ID
 * @param query - Optional query parameters
 */
export const useAppJobsByApplication = (
  tenantId: string,
  applicationId: string,
  query?: AppJobListQuery
) => {
  return useQuery({
    queryKey: ['appJobs', tenantId, 'application', applicationId, query],
    queryFn: async () => {
      const result = await appJobService.listAppJobsByApplication(tenantId, applicationId, query);
      return result;
    },
    enabled: !!tenantId && !!applicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
